import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { sendParticipantActivity } from '../../api/instantmessage-control';
import { useSelector } from '../../hook-utils';
import { openSmartSearchDialogTeams } from '../../legacy';
import { EntityType, ExtensionStatus, ExtensionSubStatus, InstantMessagerParticipantEndpointScheme } from '../../model';
import { actions } from '../../store';
import { selectConfigProp, selectMyself } from '../../store/reducers/config';
import { getInstantMessageById } from '../../store/reducers/instantmessager';
import { useMediaScripts } from '../AgentView';
import HtmlEditor, { HtmlEditorApi } from '../HtmlEditor';
import { ReactComponent as AddPeopleIcon } from '../icons/add-people.svg';
import './InstantMessagerView.scss';
import { ConversationParticipant } from './ConversationParticipant';
import { EditableText } from '../AgentView/TasksPage';

export function InstantMessagerView({ id }: { id: string }) {
  const mySelf = useSelector(selectMyself)
  const ref = useRef<HTMLDivElement>(null)
  const editorRef = React.useRef<HtmlEditorApi | null>(null)
  const dispatch = useDispatch()
  const selected = useSelector(s => getInstantMessageById(s, id))
  const [text, setText] = useState('')
  const triggered = useRef(false)
  const socketId = useSelector(s => selectConfigProp(s, 'socketId'))

  const scrollMessages = useCallback((smooth: boolean) => {
    if (ref.current !== null) {
      ref.current.scrollTo({ top: ref.current.scrollHeight, behavior: smooth ? 'smooth' : undefined })
    }
  }, [])

  // on first load scroll to the end without animation
  useEffect(() => {
    scrollMessages(false)
  }, [scrollMessages])

  // on new message scroll with animation
  useEffect(() => {
    scrollMessages(true)
  }, [selected, scrollMessages]);

  useEffect(() => {
    if (selected) {
      if (text !== '' && !triggered.current && socketId) {
        triggered.current = true
        sendParticipantActivity(selected.conversationId, true, socketId)
      } else if (text === '' && triggered.current && socketId) {
        triggered.current = false
        sendParticipantActivity(selected.conversationId, false, socketId)
      } else if (selected.conversationViewOnly !== undefined && selected.conversationViewOnly) {
        setText('')
      }
    }
  }, [selected, socketId, text])

  useMediaScripts(html => {
    const editor = editorRef.current
    if (!editor) { return }

    setText(html)

    window.setTimeout(() => {
      const editor = editorRef.current
      if (editor) { editor.focus() }
    }, 100)

  })

  // HACK: win the focus war with the Notes field
  useEffect(() => {
    const timer = window.setTimeout(() => {
      const editor = editorRef.current
      if (editor) { editor.focus() }
    }, 100)
    return () => window.clearTimeout(timer)
  }, [])

  if (!selected || !mySelf) return <div></div>

  function sendMessage() {
    if (text !== '') {
      dispatch(actions.instantMessagerMessageSend(id, text.replace(/\xa0/g, ' ')))
      setText('')
    }
  }

  //just in case we haven't joined yet should only be on refresh
  if (!selected.joined) {
    dispatch(actions.changeInstantmessageView(selected.conversationId))
  }

  let html = ''
  selected.messages && selected.messages.forEach(m => {
    html += m.message
  })

  return <div className='InstantMessagerView'>
    <EditableText type='input' className='SidebarViewHeader imHeader' iconClassName='editIcon icon' value={selected.conversationTitle} onSave={text => { if (selected) dispatch(actions.requestSetConversationTitle(selected.conversationId, text)) }} icon={true} />
    <div className='participants'>{selected.conversationViewOnly === false && <div className='icon' title='Add participant to conversation' onClick={() => {
      openSmartSearchDialogTeams('instantmessage',
        mySelf,
        ExtensionStatus.None,
        ExtensionSubStatus.Normal,
        mySelf.id,
        false,
        null,
        undefined,
        (action, type, entity, number) => {

          const endPointType = type === EntityType.Extension ? InstantMessagerParticipantEndpointScheme.IpfxExtension : type === EntityType.Contact ? InstantMessagerParticipantEndpointScheme.IpfxContact : InstantMessagerParticipantEndpointScheme.IpfxEmailContact
          const endPointServer = entity !== null ? entity.server : mySelf.server
          const endPointId = entity !== null ? entity.id : number

          dispatch(actions.requestImAddConversation(selected.conversationId, { endpointScheme: endPointType, endpointServer: endPointServer, endpointDeviceId: endPointId }))
        })
    }}><AddPeopleIcon /></div>} {Object.entries(selected.participants).map(([key, value]) => <ConversationParticipant conversation={selected} participant={value} key={'particpant' + key} />)}</div>
    <div ref={ref} className='messages' dangerouslySetInnerHTML={{ __html: html }} />
    <HtmlEditor className='editor' editorRef={editorRef} disabled={selected.conversationViewOnly === true} value={text} onChange={setText} onEnterKeyPress={sendMessage} onSendButtonClick={sendMessage} urgent={true} />
  </div>
}
